import { useState } from "react"
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { Button, Alert, Space, Typography } from "@mimber/ui-web"
import { paymentMethodsKeys } from "@mimber/client/mimber-organization"
import { useQueryClient } from "@tanstack/react-query"
import type { AddPaymentMethodFormProps } from "./AddPaymentMethodForm.types"

const { Title, Paragraph } = Typography

export const AddPaymentMethodForm: React.FunctionComponent<
  AddPaymentMethodFormProps
> = ({ onSuccess, onCancel }) => {
  const stripe = useStripe()
  const elements = useElements()
  const queryClient = useQueryClient()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)
    setErrorMessage(null)

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing?payment-method-added=true`,
        },
        redirect: "if_required",
      })

      if (error) {
        setErrorMessage(error.message || "Failed to add payment method")
      } else {
        // Success! Invalidate queries to refresh the list
        // This will trigger a refetch which includes autoSync=true
        await queryClient.invalidateQueries({
          queryKey: paymentMethodsKeys.findAll(),
        })
        onSuccess?.()
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "An error occurred"
      )
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Space orientation="vertical" size="large" className="w-full">
        <div>
          <Title level={4}>Add Payment Method</Title>
          <Paragraph type="secondary">
            Add a credit card or bank account to pay for platform fees.
          </Paragraph>
        </div>

        {errorMessage && (
          <Alert message={errorMessage} type="error" showIcon closable />
        )}

        <div className="min-h-50">
          <PaymentElement />
        </div>

        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={isProcessing}
            disabled={!stripe || !elements}
          >
            {isProcessing ? "Processing..." : "Add Payment Method"}
          </Button>
          <Button onClick={onCancel} disabled={isProcessing}>
            Cancel
          </Button>
        </Space>
      </Space>
    </form>
  )
}
